import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAppLock } from "@/contexts/AppLockContext";

const FLAG = "sd_set_new_passcode";

// After a "Forgot passcode" reset + Google sign-in, invite the user to set a new
// passcode right away so the app doesn't stay unprotected.
export default function NewPasscodePrompt() {
  const { user } = useAuth();
  const { status, refresh } = useAppLock();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || status == null) return;
    let flagged = false;
    try { flagged = localStorage.getItem(FLAG) === "1"; } catch { /* ignore */ }
    // Only prompt when the lock is actually off (reset succeeded).
    if (flagged && !status.enabled) setOpen(true);
    else if (flagged && status.enabled) clearFlag(); // already re-enabled elsewhere
  }, [user, status]);

  const clearFlag = () => { try { localStorage.removeItem(FLAG); } catch { /* ignore */ } };

  const skip = () => { clearFlag(); setOpen(false); };

  const save = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("Passcode must be 4 to 6 digits.");
    if (pin !== confirm) return toast.error("The two passcodes don't match.");
    setBusy(true);
    try {
      await api.setAppPin(pin);
      clearFlag();
      await refresh();
      toast.success("New passcode set");
      setOpen(false);
      setPin("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save passcode");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent className="z-[110] max-w-sm border-zinc-800 bg-zinc-950" data-testid="new-passcode-prompt">
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#9B2226]/15">
            <Lock className="h-5 w-5 text-[#E0575B]" />
          </div>
          <DialogTitle className="text-zinc-50">Set a new passcode</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Your passcode was reset. Set a new one now to keep Stage Door locked, or skip and do it later in Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2.5 py-1">
          <Input inputMode="numeric" type="password" maxLength={6} placeholder="New passcode (4–6 digits)" value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="new-passcode-input" />
          <Input inputMode="numeric" type="password" maxLength={6} placeholder="Confirm passcode" value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
            className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70" data-testid="new-passcode-confirm" />
        </div>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={skip} disabled={busy}
            className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="new-passcode-skip-btn">
            Not now
          </Button>
          <Button onClick={save} disabled={busy || !pin || !confirm}
            className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="new-passcode-save-btn">
            {busy ? "Saving…" : "Set passcode"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
